// `?benchmark` (RALLY_GOAL.md §14): after the car settles on the grid (as a player sits in the menu and
// the countdown, so start-up garbage collection isn't counted as driving), the safe bot drives a
// fixed stretch from the start line while
// every frame's interval, CPU time (simulation + game logic, render submission) and GPU time
// (timestamp queries, where available) are recorded; p50/p95/p99 are published on
// window.__bench and shown on screen. Run by tools/bench.mjs in headed Chromium with vsync off.
import type * as THREE from 'three/webgpu';
import type { DriveSession } from './session';
import type { GcGuard } from './gc-guard';

export interface BenchStats {
  p50: number;
  p95: number;
  p99: number;
  mean: number;
  max: number;
  n: number;
}

export interface BenchResult {
  preset: string;
  backend: string;
  gpu: string;
  viewport: [number, number];
  pixelRatio: number;
  seconds: number;
  /** Seconds held on the grid before driving (start-up work settles, as in the real flow). */
  settle: number;
  /** Time since navigation when the drive started (s): after V8's one-off memory reducer (30 s). */
  driveFromNavigation: number;
  /** Start-line GC checkpoint before the drive (src/game/gc-guard.ts) and the reserved headroom. */
  gcCheckpoint: boolean;
  gcReservedMb: number;
  distance: [number, number];
  frame: BenchStats;
  cpuSimLogic: BenchStats;
  cpuRender: BenchStats;
  cpuTotal: BenchStats;
  gpuTime?: BenchStats;
  gpuTimestamps: boolean;
  longFrames: number;
  /** The slowest frames: time into the run (s), frame interval, CPU split (ms), stage position. */
  spikes: { t: number; frame: number; simLogic: number; render: number; s: number }[];
}

/** Preallocated per-frame samples (no array growth while measuring). */
class Samples {
  private readonly a: Float64Array;
  n = 0;
  constructor(capacity: number) {
    this.a = new Float64Array(capacity);
  }
  push(v: number): void {
    if (this.n < this.a.length) this.a[this.n++] = v;
  }
  values(): Float64Array {
    return this.a.subarray(0, this.n);
  }
}

function stats(v: Float64Array): BenchStats {
  const s = Float64Array.from(v).sort();
  const q = (p: number) => s[Math.min(s.length - 1, Math.floor(p * s.length))] ?? 0;
  let sum = 0;
  for (const v of s) sum += v;
  return { p50: q(0.5), p95: q(0.95), p99: q(0.99), mean: sum / Math.max(1, s.length), max: s[s.length - 1] ?? 0, n: s.length };
}

export class Benchmark {
  private readonly frame: Samples;
  private readonly simLogic: Samples;
  private readonly render: Samples;
  private readonly total: Samples;
  private readonly gpu: Samples;
  private checkpointStarted = false;
  private checkpointDone = false;
  private readonly spikes: BenchResult['spikes'] = [];
  private t = 0;
  private settleT = 0;
  private pacedAt = -1e9;
  private driveFromNav = 0;
  private startS = 0;
  done = false;
  private resolving = false;
  private gpuOk = true;

  constructor(
    private readonly session: DriveSession,
    private readonly renderer: THREE.WebGPURenderer,
    private readonly info: { preset: string; backend: string; gpu: string; timestamps: boolean },
    private readonly warmup = 4,
    private readonly duration = 70,
    private readonly settle = 12,
    /**
     * The grid hold also lasts until this long after navigation (ms). V8's memory reducer runs once,
     * 30 s after a page loads, as two memory-reducing full GCs (~13 + 5 ms) when the page is
     * allocating slowly: a player meets them in the menu or on the grid. Held at a display's 60 Hz
     * (vsync is off for the benchmark), the page allocates like that player, so they run here and
     * not in the measured drive, where the uncapped frame rate would postpone them to V8's 100 s
     * watchdog.
     */
    private readonly settleUntilMs = 33000,
    private readonly gc: GcGuard | null = null,
  ) {
    // Up to 1,000 fps for the whole stretch.
    const cap = Math.ceil((warmup + duration) * 1000);
    this.frame = new Samples(cap);
    this.simLogic = new Samples(cap);
    this.render = new Samples(cap);
    this.total = new Samples(cap);
    this.gpu = new Samples(cap);
  }

  /** True while the car is held on the grid before the drive. */
  get settling(): boolean {
    return this.settleT < this.settle || performance.now() < this.settleUntilMs || !this.checkpointDone;
  }

  /** While settling, frames run at 60 Hz like a player's display: true = skip this one. */
  pace(now: number): boolean {
    if (this.done || !this.settling) return false;
    if (now - this.pacedAt < 1000 / 60 - 0.5) return true;
    this.pacedAt = now;
    return false;
  }

  /** After each rendered frame: dt (s), extra game-logic CPU time outside session.frame (ms). */
  sample(dt: number, extraLogicMs: number): void {
    if (this.done) return;
    if (this.settling) {
      this.settleT += dt;
      this.driveFromNav = performance.now() / 1000;
      // Then the start-line GC checkpoint, as before a player's stage (the flow steps it).
      const now = performance.now();
      if (!this.checkpointStarted && this.settleT >= this.settle && now >= this.settleUntilMs && (!this.gc || this.gc.youngReady(now))) {
        this.checkpointStarted = true;
        this.gc?.begin(this.warmup + this.duration);
      }
      if (this.checkpointStarted && !this.gc?.running) this.checkpointDone = true;
      return;
    }
    this.t += dt;
    const s = this.session;
    if (this.t < this.warmup) {
      this.startS = s.progressS;
      return;
    }
    this.frame.push(dt * 1000);
    const simLogic = s.frameMsLastFrame - s.renderMsLastFrame + extraLogicMs;
    this.simLogic.push(simLogic);
    this.render.push(s.renderMsLastFrame);
    this.total.push(s.frameMsLastFrame + extraLogicMs);
    if (dt * 1000 > 40 && this.spikes.length < 200) this.spikes.push({ t: +(this.t - this.warmup).toFixed(2), frame: +(dt * 1000).toFixed(1), simLogic: +simLogic.toFixed(1), render: +s.renderMsLastFrame.toFixed(1), s: Math.round(s.progressS) });
    if (this.gpuOk && this.info.timestamps && !this.resolving) {
      this.resolving = true;
      this.renderer
        .resolveTimestampsAsync('render')
        .then((ms) => {
          if (typeof ms === 'number' && ms > 0) this.gpu.push(ms);
        })
        .catch(() => {
          this.gpuOk = false;
        })
        .finally(() => {
          this.resolving = false;
        });
    }
    if (this.t >= this.warmup + this.duration) this.finish();
  }

  private finish(): void {
    this.done = true;
    const c = this.renderer.domElement;
    const r: BenchResult = {
      preset: this.info.preset,
      backend: this.info.backend,
      gpu: this.info.gpu,
      viewport: [c.width, c.height],
      pixelRatio: this.renderer.getPixelRatio(),
      seconds: this.duration,
      settle: +this.settleT.toFixed(1),
      driveFromNavigation: +this.driveFromNav.toFixed(1),
      distance: [Math.round(this.startS), Math.round(this.session.progressS)],
      frame: stats(this.frame.values()),
      cpuSimLogic: stats(this.simLogic.values()),
      cpuRender: stats(this.render.values()),
      cpuTotal: stats(this.total.values()),
      gpuTime: this.gpu.n > 10 ? stats(this.gpu.values()) : undefined,
      gpuTimestamps: this.gpu.n > 10,
      longFrames: this.frame.values().filter((f) => f > 50).length,
      gcCheckpoint: !!this.gc && this.gc.collected > 0,
      gcReservedMb: this.gc?.reservedMb ?? 0,
      spikes: this.spikes,
    };
    (window as unknown as { __bench: BenchResult }).__bench = r;
    const el = document.createElement('pre');
    el.className = 'bench-result';
    const f = (x: BenchStats) => `p50 ${x.p50.toFixed(2)}  p95 ${x.p95.toFixed(2)}  p99 ${x.p99.toFixed(2)} ms`;
    el.textContent = [
      `BENCHMARK ${r.preset.toUpperCase()}  ${r.backend}  ${r.gpu}  ${r.viewport.join('×')} @${r.pixelRatio}`,
      `frame      ${f(r.frame)}  (${r.frame.n} frames, ${r.longFrames} > 50 ms)`,
      `cpu sim+logic ${f(r.cpuSimLogic)}`,
      `cpu render ${f(r.cpuRender)}`,
      r.gpuTime ? `gpu        ${f(r.gpuTime)}` : 'gpu        timestamps unavailable',
      `stretch    ${r.distance[0]} → ${r.distance[1]} m`,
    ].join('\n');
    document.getElementById('ui')?.appendChild(el);
  }
}
